import type { ReactNode } from 'react';
import {
  marketersAccountDetailsImage,
  marketersInvestMonths,
  marketersPaymentReceived,
  marketersUsdAccount as content,
  type UsdAccountCard,
} from '~/content/marketers';
import { Section, SectionHeading } from '~/components/layout';
import { SmartImage } from '~/components/ui';

/**
 * "Your USD Account" three-card row (Figma 1272:49189 / mobile 1310:54603).
 * Desktop: three squares on the 9-column grid. Mobile: a horizontal swipe track.
 *
 * The card illustrations are drawn on the 451px design square and scale with
 * the card: `cqw` sets 1em to one design pixel, so every inner size is in em.
 */
export function MarketersUsdAccount() {
  return (
    <Section className="md:py-20">
      <SectionHeading title={content.title} className="lg:max-w-[455px]" />
      <div className="-mx-5 mt-10 flex snap-x snap-mandatory scroll-px-5 gap-5 overflow-x-auto px-5 [-ms-overflow-style:none] [scrollbar-width:none] md:mx-0 md:grid md:grid-cols-9 md:overflow-visible md:px-0 [&::-webkit-scrollbar]:hidden">
        {content.cards.map((card) => (
          <article key={card.id} className="w-[261px] shrink-0 snap-start md:col-span-3 md:w-auto">
            <div className="@container relative aspect-square overflow-hidden bg-red-900">
              <div className="absolute inset-0 text-[length:calc(100cqw/451)]">
                {cardArt[card.id]}
              </div>
            </div>
            <h3 className="mt-[31px] text-body font-semibold md:mt-[34px] md:text-h4">
              {card.title}
            </h3>
            <p className="mt-5 text-[15px] leading-[1.32] tracking-[0.27px] text-grey-900 md:mt-[29px] md:text-body">
              {card.description}
            </p>
          </article>
        ))}
      </div>
    </Section>
  );
}

const cardArt: Record<UsdAccountCard['id'], ReactNode> = {
  'get-paid': <PaymentReceivedArt />,
  'skip-fx': (
    <SmartImage image={marketersAccountDetailsImage} className="absolute inset-0 size-full" />
  ),
  invest: <InvestGraphArt />,
};

/** Stacked "Payment Received" notification (Figma 1272:49193). */
function PaymentReceivedArt() {
  return (
    <div className="absolute top-[181em] left-[50em] h-[81em] w-[351em]">
      <span className="absolute top-[25.7em] left-[21.45em] h-[74.4em] w-[309.2em] rounded-[23.25em] bg-[#901d1d]" />
      <span className="absolute top-[60.58em] left-[57.49em] h-[58.13em] w-[237.15em] rounded-[17.44em] bg-[rgba(143,53,53,0.45)]" />
      <div className="absolute inset-0 flex items-center gap-[16em] rounded-[21.42em] bg-white ps-[15em] pe-[16em]">
        <span className="relative size-[51.42em] shrink-0 overflow-hidden rounded-[12.85em] bg-[#f0f0f0]">
          <img
            src="/images/marketers/nsave-mark.png"
            alt=""
            loading="lazy"
            className="absolute top-[2.75em] left-[3.67em] size-[44.07em] max-w-none mix-blend-lighten"
          />
          <img
            src="/images/marketers/paypal.png"
            alt=""
            loading="lazy"
            className="absolute top-[10.12em] left-[-5.35em] h-[34.59em] w-[65.89em] max-w-none object-cover"
          />
        </span>
        <span className="flex-1 text-[14.71em] leading-[1.2] font-semibold tracking-[-0.01em] text-black">
          {marketersPaymentReceived.label}
        </span>
        <span className="text-[14.71em] leading-[1.16] font-semibold tracking-[-0.006em] whitespace-nowrap text-grey-1000">
          {marketersPaymentReceived.amount}
        </span>
      </div>
    </div>
  );
}

/** Investment graph card (Figma 1272:49407). */
function InvestGraphArt() {
  return (
    <div className="absolute top-[104em] left-[81em] h-[243em] w-[289em] rounded-[15.85em] bg-white">
      <img
        src="/images/marketers/invest-graph.svg"
        alt=""
        loading="lazy"
        className="absolute top-[2.57em] left-0 h-[207.18em] w-[288.57em] max-w-none"
      />
      {marketersInvestMonths.map((month, i) => (
        // Positioned wrapper stays on the 1em = 1px scale; only the label text is resized.
        <span
          key={month}
          style={{ left: `${monthOffsets[i]}em` }}
          className="absolute bottom-[18.6em]">
          <span className="block text-[10.36em] leading-[1.4] font-medium tracking-[0.014em] text-grey-700">
            {month}
          </span>
        </span>
      ))}
    </div>
  );
}

const monthOffsets = [31.08, 99.89, 167.22, 236.77];
